import type { AppLabels } from "../types";

export const en: AppLabels = {
  /* ── Common ── */
  common: {
    cancel: "Cancel",
    save: "Save",
    close: "Close",
    confirm: "Confirm",
    remove: "Remove",
    back: "Back",
    next: "Next",
    submit: "Submit",
    loading: "Loading…",
    errorGeneric: "Something went wrong. Please try again.",
  },

  /* ── Auth ── */
  auth: {
    login: "Log in",
    signup: "Sign up",
    logout: "Log out",
    createAccount: "Create account",
    welcomeBack: "Welcome back",
    createYourAccount: "Create your account",
    signInSubtitle: "Sign in to continue to Hivly",
    signUpSubtitle: "Get started with Hivly for free",
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: "Already have an account?",
    email: "Email",
    emailPlaceholder: "you@company.com",
    password: "Password",
    passwordPlaceholder: "Min. 8 characters",
    passwordMask: "••••••••",
    fullName: "Full name",
    fullNamePlaceholder: "Jane Doe",
    companyName: "Company name",
    companyNamePlaceholder: "Optional",
    showPassword: "Show password",
    hidePassword: "Hide password",
    validation: {
      emailRequired: "Email is required",
      emailInvalid: "Enter a valid email",
      passwordRequired: "Password is required",
      passwordMinLength: "At least {min} characters",
      nameRequired: "Name is required",
    },
  },

  /* ── Layout ── */
  layout: {
    nav: {
      home: "Home",
      agents: "Agents",
      about: "About",
      organization: "Organization",
      createAgent: "Create Agent",
    },
    header: {
      project: "Project",
      accountMenu: "Account menu",
    },
    footer: {
      copyright: "{year} Client Project. All rights reserved.",
    },
  },

  /* ── Agent ── */
  agent: {
    page: {
      title: "Agents",
      subtitle: "Manage and configure your AI-powered agents.",
      total: "Total",
      active: "Active",
      step: "step",
      steps: "steps",
      previousPage: "Previous page",
      nextPage: "Next page",
    },
    empty: {
      title: "No agents yet",
      description:
        "Create your first AI agent to start automating conversations.",
    },
    tabs: {
      configuration: "Configuration",
      analytics: "Analytics",
      backToAgents: "Back to agents",
      openRoom: "Open room",
      testConversation: "Test",
    },
    settings: {
      title: "General Settings",
      active: "Active",
      inactive: "Inactive",
      identity: "Identity",
      agentName: "Agent name",
      agentNamePlaceholder: "e.g. Senior Engineer Interview",
      voice: "Voice",
      languageCode: "Language code",
      languageCodePlaceholder: "Select a language…",
      voiceName: "Voice name",
      voiceNamePlaceholder: "Select a voice…",
      chooseLanguageFirst: "Choose a language first",
      gender: "Gender",
      render: "Render",
      presentationLink: "Presentation Code",
      presentationLinkPlaceholder:
        "1H3bMHO-Ywn4oEvvqBUZJE2BlFcDhD3n4VaKoW-kC7AE",
      startFromTemplate: "Start from template",
      processing: "Processing",
      conversationType: "Conversation type",
      conversationTypePlaceholder: "Select a conversation type…",
      chooseConversationTypeFirst: "Choose a conversation type first",
      summaryType: "Summary type",
      summaryTypePlaceholder: "Select a summary type…",
      statisticsType: "Statistics type",
      statisticsTypePlaceholder: "Select a statistics type…",
      chooseSummaryFirst: "Choose a summary type first",
      features: "Features",
      lipSync: "Lip sync",
      sessionPersistence: "Session persistence",
      autoSummary: "Auto summary",
      videoRecording: "Video recording",
      saveSettings: "Save settings",
    },
    assessment: {
      title: "Assessment",
      noAssessment: "No assessment configured for this agent.",
      generalSettings: "General Settings",
      language: "Language",
      languagePlaceholder: "e.g. TypeScript",
      durationSeconds: "Duration (seconds)",
      testContent: "Test content",
      testContentPlaceholder: "Markdown-formatted assessment content…",
      saveAssessment: "Save assessment",
    },
    steps: {
      addStep: "Add Step",
      addStepSubtitle: "Select a step type to add to the pipeline",
      setCategoryHint:
        "Set a Conversation Type in General Settings to enable step catalog.",
      noStepTypes: "No step types found for this category.",
      alreadyInPipeline: "Already in pipeline",
      addingStep: "Adding step…",
      stepNotFound: "Step not found",
      generalSettings: "General Settings",
      saveChanges: "Save changes",
      removeStep: "Remove step",
      removeStepConfirm: "Remove step?",
      removeStepPrefix: "This will permanently remove ",
      removeStepSuffix: " from the pipeline. This action cannot be undone.",
    },
    analytics: {
      totalSessions: "Total Sessions",
      completionRate: "Completion Rate",
      completed: "Completed",
      abandoned: "Abandoned",
      active: "Active",
      noStatistics: "No statistics available yet.",
      calculate: "Calculate",
      recalculate: "Recalculate statistics",
      all: "All",
      session: "session",
      sessions: "sessions",
      noMatchingSessions: "No matching sessions",
      noSessionsYet: "No sessions yet",
      noFilteredDescription:
        "No {status} sessions found. Try a different filter.",
      noSessionsDescription:
        "Sessions will appear here once participants interact with this agent.",
      participant: "Participant",
      emailHeader: "Email",
      status: "Status",
      stepHeader: "Step",
      lastActivity: "Last Activity",
      noMessages: "No messages in this conversation.",
      justNow: "just now",
      minutesAgo: "{n}m ago",
      hoursAgo: "{n}h ago",
      daysAgo: "{n}d ago",
      deleteSession: "Delete session",
      deleteSessionConfirm:
        "Are you sure you want to delete this session? This action cannot be undone.",
      deleting: "Deleting…",
      regenerateSummary: "Regenerate summary",
      regeneratingSummary: "Regenerating…",
      generateSummary: "Generate summary",
      noSummaryAvailable: "No summary available",
      noSummaryDescription:
        "A summary will appear here once generated for this session.",
    },
    generate: {
      pageTitle: "Create Agent",
      pageSubtitle: "Configure and generate a new AI-powered agent.",
      stepAgentInfo: "Agent Info",
      stepAgentConfig: "Configuration",
      stepPipeline: "Pipeline Steps",
      stepReview: "Review & Generate",
      agentName: "Agent name",
      agentNamePlaceholder: "e.g. Senior Engineer Interview",
      summary: "Summary",
      summaryPlaceholder:
        "Describe the role, responsibilities, and requirements…",
      voice: "Voice",
      render: "Render",
      features: "Features",
      processing: "Processing",
      selectSteps: "Select pipeline steps",
      selectStepsHint: "Choose the steps for your agent's conversation flow.",
      noConversationType:
        "Select a conversation type in the previous step first.",
      additionalDataTitle: "Additional configuration",
      assessmentType: "Assessment type",
      timeMinutes: "Duration (minutes)",
      languageCoding: "Coding language",
      targetLanguage: "Target language",
      reviewTitle: "Review your agent",
      reviewHint: "Verify all settings before generating your agent.",
      generating: "Generating agent…",
      generateAgent: "Generate Agent",
      errorGenerate: "Failed to generate agent. Please try again.",
      back: "Back",
      next: "Next",
      stepOf: "Step {current} of {total}",
    },
  },

  /* ── Organization ── */
  organization: {
    page: {
      title: "Organization",
      subtitle: "Manage your organization, members, and roles.",
      createOrg: "Create Organization",
      noOrganizations: "No organizations yet",
      noOrganizationsDescription:
        "Create your first organization or ask to be invited to one.",
    },
    form: {
      name: "Organization name",
      namePlaceholder: "e.g. Acme Inc.",
      logoUrl: "Logo URL",
      logoUrlPlaceholder: "https://example.com/logo.png",
      createTitle: "Create Organization",
      editTitle: "Edit Organization",
      saving: "Saving…",
    },
    members: {
      title: "Members",
      addMember: "Add member",
      email: "Email",
      emailPlaceholder: "member@company.com",
      role: "Role",
      rolePlaceholder: "Select a role",
      roleAdmin: "Admin",
      roleWrite: "Write",
      roleRead: "Read",
      removeMember: "Remove member",
      removeMemberConfirm: "Are you sure you want to remove this member?",
      changeRole: "Change role",
      noMembers: "No members yet.",
      addedAt: "Added",
    },
    actions: {
      leaveOrg: "Leave organization",
      leaveOrgConfirm:
        "Are you sure you want to leave this organization? You will lose access to all shared resources.",
      deactivateOrg: "Deactivate organization",
    },
    switcher: {
      personalMode: "Personal",
      switchToOrg: "Switch to organization",
      switchToPersonal: "Switch to personal",
      currentOrg: "Current organization",
      organizations: "Organizations",
    },
    validation: {
      nameRequired: "Organization name is required",
      emailRequired: "Email is required",
      emailInvalid: "Enter a valid email",
      roleRequired: "Role is required",
    },
  },

  /* ── Room ── */
  room: {
    interviewModal: {
      previewMode: "Preview",
      readyToBegin: "Interview",
      draftMode: "Draft Mode",
      draftModeMessage:
        "Only campaign creators can start interviews in draft mode.",
      loggedInAs: "Logged in as",
      fullName: "Full Name",
      fullNamePlaceholder: "Enter your full name",
      emailAddress: "Email Address",
      emailPlaceholder: "Enter your email address",
      emailHelp: "We'll send your report to this email address",
      startInterview: "Start Interview",
      webcamNotice: "The interview will be recorded.",
      termsText: "By continuing, you agree to our",
      termsLink: "Terms of Service",
      and: "and",
      privacyLink: "Privacy Policy",
      errorCreate: "Failed to create interview. Please try again.",
      errorAuth: "Authentication failed. Please try again.",
      creatingInterview: "Creating Interview",
      agentNotActive: "This agent is not currently active.",
      invalidEmail: "Please enter a valid email address.",
    },
    tutorial: {
      header: {
        welcome: "Welcome to your Interview",
        subtitle: "A quick guide before you begin",
      },
      deviceSetup: {
        title: "Device Setup",
        subtitle: "Let's set up your camera and microphone for the interview.",
      },
      recording: {
        title: "Voice Interaction",
        description:
          "Press and hold the microphone button to speak to the AI interviewer. Release when you're done speaking.",
      },
      chat: {
        title: "Chat History",
        description:
          "Click the chat icon to view the conversation history at any time.",
      },
      reports: {
        title: "Interview Report",
        description:
          "At the end of the interview, you'll receive an AI-generated report.",
        footer: "Review your performance",
      },
      card: {
        howToUse: "How to use",
        pressHoldToSpeak: "Press & hold to speak",
        voiceTranscribed:
          "Your voice will be transcribed and sent to the AI interviewer",
        clickViewChat: "Click to view chat history",
        finalStep: "Final Step",
      },
      buttons: {
        back: "Back",
        next: "Next",
        tutorial: "Continue",
        startInterview: "Begin Interview",
      },
      permissions: {
        microphone: "Microphone",
        camera: "Camera",
        grantAccess: "Grant Access",
      },
      chatExample1: "Tell me about your experience with React.",
      chatExample2: "I've been working with React for three years…",
      interviewReport: "Interview Report",
      strengths: "Strengths",
    },
    room: {
      confirmTitle: "Confirm Report Creation",
      confirmMessage:
        "Create report now? If you proceed, the interview will end and the report will be generated using only the data collected so far.",
      cancel: "Cancel",
      confirmSubmit: "Confirm Submit",
      createReport: "Create Report",
      creatingReport: "Creating Report...",
      chat: "Chat",
    },
    speechControls: {
      tapToStop: "Tap to stop",
      processing: "Processing…",
      aiSpeaking: "AI is speaking…",
      tapToSpeak: "Tap to speak",
      stopRecording: "Stop recording",
      startRecording: "Start recording",
    },
    testModal: {
      assessment: "Assessment",
      writtenResponse: "Written response",
      answerPlaceholder: "Write your answer here...",
      lessThanMinute: "Less than a minute remaining",
      minutesRemaining: "{n} min remaining",
      confirmSubmission: "Confirm submission",
      confirmSubmissionMessage:
        "You won't be able to edit your answer after submitting. Are you sure you're ready?",
    },
    loadingMessages: [
      "Analyzing interview data...",
      "Generating insights...",
      "Compiling report...",
      "Finalizing details...",
      "Almost there...",
    ],
    conclusion: {
      closeWindow: "Close window",
      safeToClose: "You can safely close this tab",
    },
    agentNotFound: "Agent not found.",
    agentNotFoundSub:
      "This agent may be inactiver the link is no longer valid.",
    speechTip:
      "Press and hold the button to start recording. Release the button when you're done speaking.",
    warningWait: "Please wait for the AI to finish speaking.",
    errorGeneric: "Something went wrong. Please try again.",
    conclusionTitle: "Interview Complete",
    conclusionMessage:
      "Thank you for completing the interview. Your report will be generated shortly.",
  },
};
